import { CustomerStoriesSlickCarousel } from 'components/customerStories/CustomerStoriesSlickCarousel';
import EndOfPageCtaWithForm from 'components/generic/EndOfPageCTAwithForm';
import { SectionWithImageOrVideo } from 'components/generic/SectionWithImageOrVideo';
import HeroBanner from 'components/heros/homePage/HeroBanner';
import { Layout } from 'components/layout';

import { MetaDataWrapper } from 'components/tsxCommon/MetaDataWrapper';
import { PlayerTypes } from 'components/tsxCommon/PlayerTypes';
import { Section } from 'components/tsxCommon/Section';
import { graphql } from 'gatsby';
import React from 'react';
import { useInView } from 'react-intersection-observer';
import BoostProductivityHoverIcon from '../../static/images/svg/navIcons/boost-productivity-hover.inline.svg';
import BoostProductivityIcon from '../../static/images/svg/navIcons/boost-productivity.inline.svg';
import BroadcastWinsHoverIcon from '../../static/images/svg/navIcons/broadcast-wins-homepage-hover.inline.svg';
import BroadcastWinsIcon from '../../static/images/svg/navIcons/broadcast-wins-homepage.inline.svg';
import InstillAccountabilityHoverIcon from '../../static/images/svg/navIcons/instill-accountability-hover.inline.svg';
import InstillAccountabilityIcon from '../../static/images/svg/navIcons/instill-accountability.inline.svg';

import { VideoHero } from 'components/tsxHeros/VideoHero';
import { KpiBox } from 'components/tsxCommon/KpiBox';
import { CenteredSectionWithChildren } from 'components/tsxCommon/CenteredSectionWithChildren';
import { FeatureBox } from 'components/tsxCommon/FeatureBox';

import { SymbolsBg } from 'components/tsxCommon/SymbolsBg';

import g2CrowdGif from '../../static/images/g2-2025-banner.jpg';
import { FeatureCircle } from 'components/tsxCommon/FeatureCircle';
import { Button } from 'components/tsxCommon/Button';

const HomeUpdate = ({ data }) => {
    const {
        achiever,
        socialite,
        killer,
        explorer,
        videoPoster,
        acrisureThumbnail,
        productTourPoster
    } = data;

    const [ref, inView] = useInView({
        threshold: 0.0,
        initialInView: true
    });

    const unsupported =
        typeof window !== 'undefined' && typeof window.IntersectionObserver === 'undefined';
    const visible = unsupported ? true : inView;

    return (
        <Layout relative={visible} noMarginTop includeMobileLogo invertLogo>
            <MetaDataWrapper pageKey="homepage" />
            <div ref={!unsupported ? ref : undefined} className="tw-bg-neutral-inverse">
                <VideoHero
                    videoViewTrackingEvent="view_salesscreen_intro"
                    header={
                        <span className="lg:tw-text-6xl">
                            LOVE <span className="tw-text-primary">THE</span> GAME
                        </span>
                    }
                    paragraph="Motivate every sales rep to achieve their goals using the power of
                            gamification. SalesScreen boosts daily activities and visualizes
                            progress so your entire team can see the path to success."
                    videoUrl="https://content.salesscreen.com/hermes-cms/salesscreen-intro.mp4"
                    poster={videoPoster}
                    playButtonText="Get your team to goal"
                    cta={<Button buttonVariant='internalLink' variant='primary' text='Take an interactive tour' url='/demo-center'/>}
                />
            </div>
            <Section spacing="none">
                <HeroBanner />
            </Section>
            <div className="container">
                <Section spacing="narrow">
                    <div className="tw-grid lg:tw-items-stretch lg:tw-grid-cols-3 tw-grid-cols tw-gap-48 tw-justify-center tw-select-none">
                        <FeatureBox
                            icon={<BoostProductivityIcon className="tw-w-64 tw-h-64" />}
                            hoverIcon={<BoostProductivityHoverIcon className="tw-w-64 tw-h-64" />}
                            link="boost-productivity"
                            paragraph="Create a variety of competitions that keep your players striving. Cheer them on with in-feed chatter, GIFs, and more."
                            title="Boost Productivity"
                        />
                        <FeatureBox
                            icon={<InstillAccountabilityIcon className="tw-w-64 tw-h-64" />}
                            hoverIcon={
                                <InstillAccountabilityHoverIcon className="tw-w-64 tw-h-64" />
                            }
                            link="instill-accountability"
                            paragraph="Share real-time data on our easy-to-read dashboards so players can stay on track to meet, and exceed, their goals."
                            title="Instill Accountability"
                        />
                        <FeatureBox
                            icon={<BroadcastWinsIcon className="tw-w-64 tw-h-64" />}
                            hoverIcon={<BroadcastWinsHoverIcon className="tw-w-64 tw-h-64" />}
                            link="recognize-hard-work"
                            paragraph="Reward ongoing effort with achievement badges and coins for the reward shop to keep morale high."
                            title="Recognize Hard Work"
                        />
                    </div>
                </Section>
            </div>
            <Section>
                <SectionWithImageOrVideo
                    title="Acrisure increased new revenue by 45% year-over-year after implementing SalesScreen"
                    paragraph="Hear how Rob Erfurt, Director of Sales Strategy at Acrisure, achieved impressive year over year growth by leveraging SalesScreen to train and motivate their sales reps."
                    videoUrl="https://content.salesscreen.com/hermes-cms/acrisure_growth_45percent.mp4"
                    videoPoster={acrisureThumbnail}
                    even={true}
                    ctaText="Read Acrisure's success story "
                    ctaUrl="/customers/how-acrisure-achieved-45-yearoveryear-growth-in-new-revenue-using-salesscreen/?utm_campaign=cm_acrisure_rob_accelerator&utm_source=ss_inline_promo&utm_medium=cta_home_cs_block"
                />
            </Section>
            <Section className="tw-relative">
                <SymbolsBg />
                <FeatureCircle
                    title={
                        <>
                            Tap into the drivers that motivate
                            <br /> the individuals on your team
                        </>
                    }
                    paragraph=" Gamification enables sales managers to engage their team by tapping into
                            the drivers that motivate every individual. Easy-to-read dashboards,
                            pre-built competitions, achievement badges, and more provide a wide
                            range of motivation tools for all kinds of reps."
                />
            </Section>
            <Section spacing="none">
                <CenteredSectionWithChildren
                    backgroundClassname="tw-bg-neutral-inverse"
                    onDark={true}
                    ctaVariant="primary"
                    useBannerBg={true}
                    children={
                        <div className="tw-grid lg:tw-grid-cols-2 lg:tw-gap-48 tw-gap-24 tw-justify-center tw-items-start tw-mt-48">
                            <KpiBox
                                iconType="plus"
                                number={30}
                                symbol="%"
                                text={
                                    <>
                                        KPI <span className="tw-text-primary-dark">Increase</span>
                                    </>
                                }
                                paragraph="In the first 6 months of using SalesScreen"
                            />
                            <KpiBox
                                number={44}
                                symbol="%"
                                text={
                                    <>
                                        Activity{' '}
                                        <span className="tw-text-primary-dark">Increase</span>
                                    </>
                                }
                                paragraph="When running SalesScreen Competitions"
                            />
                        </div>
                    }
                />
            </Section>
            <Section>
                <SectionWithImageOrVideo
                    title="SalesScreen named Sales Gamification Leaders by G2"
                    paragraph="We love a leaderboard and have been staying on top of the quadrant as sales gamification leaders on the G2 Grid. From our white-glove service to our industry-leading visualization tools, we’ve worked tirelessly to break the mold on what you can expect from a SaaS provider."
                    customImageComponent={<img src={g2CrowdGif} alt="G2 Crowd" />}
                    ctaText="Read 200+ reviews on G2"
                    ctaUrl="https://www.g2.com/products/salesscreen/reviews"
                />
            </Section>

            <Section spacing="onlyPadding">
                <div className="container">
                    <PlayerTypes
                        title={
                            <>
                                <span className="sm:tw-block tw-hidden">
                                    Your business is a multiplayer game.
                                    <br /> Who’s on your team?
                                </span>
                                <span className="tw-block sm:tw-hidden">
                                    Your business is a multiplayer game. Who’s on your team?
                                </span>
                            </>
                        }
                        paragraph={
                            <>
                                <span className="sm:tw-block tw-hidden">
                                    Great managers know that not every player is the same. <br />
                                    SalesScreen knows how to motivate them all.
                                </span>
                                <span className="tw-block sm:tw-hidden">
                                    Great managers know that not every player is the same.
                                    SalesScreen knows how to motivate them all.
                                </span>
                            </>
                        }
                        playerTypes={[
                            {
                                title: 'Achievers',
                                paragraph:
                                    'These players want to show off their skills, reach established goals, and earn elite status.',
                                image: achiever,
                                keyDrivers: [
                                    { title: 'In-game Achievements', icon: 'events' },
                                    { title: 'Levels Unlocked', icon: 'level' }
                                ]
                            },
                            {
                                title: 'Explorers',
                                paragraph:
                                    'No side quest is too obscure for these players, who are all about discovery.',
                                image: explorer,
                                keyDrivers: [
                                    { title: 'Niche Badges', icon: 'achievement' },
                                    { title: 'New Contest formats', icon: 'competition' }
                                ]
                            },
                            {
                                title: 'Socialites',
                                paragraph:
                                    'These players want to collaborate and build community while striving toward team goals.',
                                image: socialite,
                                keyDrivers: [
                                    { title: 'Social Feeds', icon: 'message' },
                                    { title: 'Celebrations', icon: 'star' }
                                ]
                            },
                            {
                                title: 'Killers',
                                paragraph:
                                    'These are the ultimate peer-to-peer competitors. They want their name at the top of the leaderboard.',
                                image: killer,
                                keyDrivers: [
                                    { title: 'Leaderboards and Rankings', icon: '1st' },
                                    { title: '1v1 Battles', icon: 'battle' }
                                ]
                            }
                        ]}
                    />
                </div>
            </Section>
            <CustomerStoriesSlickCarousel
                backgroundColor="#222"
                backgroundClassname="tw-bg-neutral-inverse"
            />
            <EndOfPageCtaWithForm
                useBannerBg={true}
                header="Ready to tap into the motivation that gets your sales team to goal?"
                paragraph="Speak to one of our sales gamification experts to discover how to maximize your team's outcomes."
            />
        </Layout>
    );
};

export const query = graphql`
    query {
        acrisureThumbnail: file(relativePath: { eq: "homepage/acrisure-video-poster.jpg" }) {
            childImageSharp {
                gatsbyImageData(placeholder: BLURRED, layout: FULL_WIDTH)
            }
        }
        videoPoster: file(relativePath: { eq: "homepage/salesscreen-intro-poster.jpg" }) {
            childImageSharp {
                gatsbyImageData(placeholder: BLURRED, layout: FULL_WIDTH)
            }
        }
        achiever: file(relativePath: { eq: "homepage/player-type-achiever-transparent.png" }) {
            childImageSharp {
                gatsbyImageData(placeholder: BLURRED)
            }
        }
        socialite: file(relativePath: { eq: "homepage/player-type-socialite-transparent.png" }) {
            childImageSharp {
                gatsbyImageData(placeholder: BLURRED)
            }
        }
        killer: file(relativePath: { eq: "homepage/player-type-killer-transparent.png" }) {
            childImageSharp {
                gatsbyImageData(placeholder: BLURRED)
            }
        }
        explorer: file(relativePath: { eq: "homepage/player-type-explorer-transparent.png" }) {
            childImageSharp {
                gatsbyImageData(placeholder: BLURRED)
            }
        }
        productTourPoster: file(
            relativePath: { eq: "pages/industry/insurance/product-tour-poster.jpg" }
        ) {
            childImageSharp {
                gatsbyImageData(placeholder: BLURRED)
            }
        }
    }
`;

export default HomeUpdate;
